import { GlobalDialogType } from 'components/Modals/GlobalDialog'
import { createModal } from 'components/Modals/ModalFactory'
import { ModalWrapper } from 'components/Modals/ModalWrapper'
import Translation from 'components/Translation'
import { AuthContext, useAuth } from 'context/AuthContext'
import { useEffect, useState } from 'react'
import {
    confirmMembershipPayment,
} from 'services/memberships-client'
import { LocationItem } from 'types/location'
import { MembershipApiItem } from 'types/membership'
import { ServiceApiItem } from 'types/service'
import { classNames } from 'utils/dom-helpers'
import { useFetch } from 'utils/fetch-helper'
import { toCurrency } from 'utils/string-localization-helper'
import { FetchMethods } from 'utils/types'
import { useAppContext } from 'context/AppContext'
import { useTenantContext } from 'context/TenantContext'
import { getTimePeriodShort } from 'utils/booking-helper'
import { useAppErrorApiContext } from 'context/AppErrorContext'
import { OrderMembershipForFree } from 'services/memberships'

const ModalProvider = createModal(
    AuthContext,
    'PurchaseSummaryModal',
    () => (
        <>
            <i className="feather feather-plus mr-4" />
            <Translation
                render_as="span"
                className="circular"
                content_key="purchase_summary"
            />
        </>
    ),
    () => (
        <span className="inline-block w-6 h-6 text-primary text-center font-extralight">
            <i className="feather feather-x " />
        </span>
    )
)

export const PurchaseSummaryModalOpener = ModalProvider.Opener
export const PurchaseSummaryModalCloser = ModalProvider.Closer

export default function PurchaseSummaryModal() {
    const { setGlobalError } = useAppErrorApiContext()
    const { setGlobalLoading } = useAppContext()
    const {
        CreateClientModal,
        PurchaseSummaryModal,
        GlobalDialog,
    } = useAuth()
    const { tenant } = useTenantContext()
    const { attributes, setAttributes } = PurchaseSummaryModal
    const [agree, toggleAgree] = useState<boolean>(false)
    const [services, setServices] = useState<string[]>([])
    const membership = attributes?.selected_membership as MembershipApiItem
    const location = attributes?.primary_location as LocationItem
    const total_amount = membership?.price + membership?.additionalFee

    let redemption_type = 'Classes'
    let redemption_value = membership?.redemptionServices

    if (membership?.redemptionAmount) {
        redemption_type = ''
        redemption_value = membership?.redemptionAmount
    } else if (membership?.redemptionMinutes) {
        redemption_type = 'Minutes'
        redemption_value = membership?.redemptionMinutes
    }

    const { data: services_api_response } = useFetch(
        `/v1/services?tenantId=${tenant?.id}&isDeleted=false`,
        FetchMethods.GET,
        !!tenant?.id && PurchaseSummaryModal.is_open
    )

    useEffect(() => {
        if (!PurchaseSummaryModal.is_open) {
            toggleAgree(false)
            setAttributes({})
        }
    }, [PurchaseSummaryModal.is_open])

    useEffect(() => {
        if (services_api_response.content && membership) {
            let categoryIds = membership?.categoryIds || []
            let serviceIds = membership?.serviceIds || []
            let selected_services: string[] = []
            services_api_response.content.map((service: ServiceApiItem) => {
                if (
                    categoryIds.indexOf(service.category.id) > -1 ||
                    serviceIds.indexOf(service.id) > -1
                ) {
                    selected_services.push(service.name)
                }
            })
            setServices(selected_services)
        }
    }, [services_api_response, membership])

    const handlePayment = async () => {
        try {
            setGlobalLoading(true)
            const tenant_id = tenant?.id as string
            const client_id = CreateClientModal.attributes?.id as string
            const membership_id = membership?.id as string
            let api
            if (total_amount == 0) {
                api = await OrderMembershipForFree(
                    client_id,
                    tenant_id,
                    membership_id
                )
            }
            else if (attributes?.is_cash_payment) {
                api = await confirmMembershipPayment(
                    tenant_id,
                    client_id,
                    membership_id,
                    total_amount,
                    location.id as string,
                    true,
                    'CASH'
                )
            }
            else {
                api = await confirmMembershipPayment(
                    tenant_id,
                    client_id,
                    membership_id,
                    total_amount,
                    location.id as string
                )
            }

            if (api.ok) {
                GlobalDialog.setAttributes({
                    title: 'Successfully added membership',
                    message: membership.name,
                    button: 'Back to Memberships',
                    className: 'w-auto',
                    type: GlobalDialogType.SUCCESS,
                })
                GlobalDialog.open()
                setAttributes({
                    refetch: true,
                })
                PurchaseSummaryModal.close()
            } else {
                throw new Error(api?.message)
            }
        } catch (err) {
            setGlobalError(err)
        } finally {
            setGlobalLoading(false)
        }
    }

    return (
        <ModalProvider.Visible>
            <ModalWrapper className="w-2/5">
                <div className="bg-white shadow-2xl overflow-x-hidden sm:rounded-md relative border-2 border-gray-100 my-auto">
                    <div className="flex flex-col text-gray-500 z-10 w-full p-4">
                        <PurchaseSummaryModalCloser className="text-right text-2xl" />

                        <Translation
                            render_as="span"
                            className="circular inline-block self-center text-3xl text-primary-dark mx-auto"
                            content_key="purchase_summary"
                        />
                    </div>

                    <div className="flex flex-col gap-y-5 w-full p-10 text-black">
                        <div className="flex flex-col border border-primary-light text-black rounded-xl p-5 pb-0">
                            <div className="flex flex-row justify-between ">
                                <div>
                                    <p className="font-bold">
                                        {membership?.name}
                                    </p>
                                    <p className="text-xs text-gray-400 uppercase">
                                        {membership?.membershipPackage
                                            ? 'package'
                                            : 'subscription'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-primary-light bg-primary-light bg-opacity-10 rounded-full px-2 py-1">
                                        {toCurrency(membership?.price)}
                                        {membership?.membershipPackage
                                            ? ''
                                            : ` / ${getTimePeriodShort(membership?.membershipSubscription?.timePeriod as string)}`}
                                    </p>
                                </div>
                            </div>

                            { membership?.additionalFee ? (
                                <div className="flex flex-row justify-between mt-2">
                                    <p className="font-bold">
                                        <Translation content_key="initial_purchase_fee" />
                                    </p>
                                    <p className="text-primary-light bg-primary-light bg-opacity-10 rounded-full px-2 py-1">
                                        {toCurrency(membership?.additionalFee)}
                                    </p>
                                </div>) : ('')
                            }

                            <div className="border-t my-3"></div>

                            <div className="flex flex-row justify-between my-3 text-xs">
                                <div>
                                    <p className="font-bold uppercase text-primary">
                                        <Translation content_key="redemption" />
                                    </p>
                                    <p>
                                        {redemption_value} {redemption_type}
                                    </p>
                                </div>
                                <div>
                                    <p className="font-bold uppercase text-primary">
                                        <Translation content_key="location" />
                                    </p>
                                    <p>{location?.name}</p>
                                </div>
                                {membership?.membershipSubscription && (
                                    <div>
                                        <p className="font-bold uppercase text-primary">
                                            <Translation content_key="charged" />
                                        </p>
                                        <p>
                                            {
                                                membership
                                                    ?.membershipSubscription
                                                    ?.timePeriod
                                            }
                                        </p>
                                    </div>
                                )}
                                <div>
                                    <p className="font-bold uppercase text-primary">
                                        <Translation content_key="validity" />
                                    </p>
                                    {membership?.membershipPackage ? (
                                        <p>
                                            {
                                                membership
                                                    ?.membershipPackage
                                                    ?.validityValue
                                            }{' '}
                                            {
                                                membership
                                                    ?.membershipPackage
                                                    ?.validityType
                                            }
                                        </p>
                                    ) : (
                                        <p>
                                            {
                                                membership
                                                    ?.membershipSubscription
                                                    ?.numberOfCycles
                                            }{' '}
                                            {
                                                membership
                                                    ?.membershipSubscription
                                                    ?.timePeriod
                                            }
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-col">
                                <p className="font-bold uppercase text-primary text-xs">
                                    <Translation content_key="available_services" />
                                </p>
                                <div className="flex flex-wrap my-2">
                                    {services.map((service_name) => {
                                        return (
                                            <span
                                                key={service_name}
                                                className="whitespace-nowrap bg-primary-light bg-opacity-10 text-primary-light rounded-full px-2 py-1 m-1 ml-0"
                                            >
                                                {service_name}
                                            </span>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>

                        {!attributes?.is_cash_payment && (
                                <div className="flex items-center text-left text-base">
                                    <label htmlFor="cash">
                                        <input
                                            id="cash"
                                            type="checkbox"
                                            className="h-5 w-5 mr-2 border-gray-300 rounded text-primary focus:ring-primary-light checked:bg-primary-light"
                                            onChange={(e) => {
                                                toggleAgree(
                                                    e.target.checked
                                                )
                                            }}
                                            checked={agree}
                                        />
                                        <Translation
                                            render_as="span"
                                            content_key="card_holder_permission"
                                        />{' '}
                                    </label>
                                </div>
                            )}

                        <div className="flex flex-col pt-5">
                            <div className="flex justify-between mx-auto">
                                <button
                                    type="button"
                                    className="border border-gray-300 rounded-lg inline-block mr-3 py-3 px-8"
                                    onClick={PurchaseSummaryModal.close}
                                >
                                    <Translation content_key="cancel" />
                                </button>

                                <button
                                    type="button"
                                    disabled={
                                        (attributes?.is_cash_payment as boolean)
                                            ? false
                                            : !agree
                                    }
                                    className={classNames(
                                        'group flex justify-center',
                                        'py-3 px-8 border border-transparent',
                                        'rounded-md text-white',
                                        'disabled:bg-gray-300',
                                        'bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light'
                                    )}
                                    onClick={handlePayment}
                                >
                                    {attributes?.is_cash_payment ? (
                                        <Translation content_key="confirm" />
                                    ) : (
                                        <Translation content_key="confirm_and_pay_now" />
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </ModalWrapper>
        </ModalProvider.Visible>
    )
}
